/**
 * Post-AMEFF-import review-modal.
 *
 * Na een succesvolle AMEFF-import opent deze modal automatisch wanneer
 * `result.labels` niet-leeg is. Per Label dat als `drawing.Rectangle`
 * (unclassified) of via heuristic naar een risk-stereotype is gemapt,
 * laat de modal de modelleur het type bevestigen of wijzigen. Bij
 * "Toepassen" worden alleen gewijzigde rijen ge-patcht via
 * `patchConcept`.
 *
 * Werkstroom (B-aanpak — zie commit-keuze):
 *  - Heuristic-classificatie heeft al gedraaid en concepten zijn al
 *    geïnsert. Modal corrigeert dus achteraf, geen blokkerende preview.
 *  - "Skip alles" sluit zonder wijzigingen — heuristic-keuzes blijven
 *    staan.
 *  - Modelleur kan onafhankelijk per item kiezen.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../registry";
import { useWorkspaceData } from "../state/workspace";

interface AmeffLabel {
  concept_id: string;
  name: string;
  type_id: string;
  stereotypes: string[];
  source: "heuristic" | "unclassified";
}

interface AmeffDiagram {
  diagram_id: string;
  name: string;
  view_kind: string;
  source: "heuristic";
}

/** Een concept waarvan de 3.1→4-conversie meer dan één geldig doeltype had.
 *  Het compat-stereotype (`archimate31.Gap`, …) legt de oorspronkelijke
 *  identiteit vast en blijft ongemoeid — alleen het basistype is een keuze. */
interface AmeffAmbiguous {
  concept_id: string;
  name: string;
  type_id: string;
  stereotype: string;
  legacy_type_id: string;
  candidates: string[];
}

/** Voorgedefinieerde stereotype-keuzes voor de Label-review-modal.
 *  Elk item bevat het stereotype-id + bijbehorend base-type-id. Voor
 *  polymorfe stereotypes (ThreatEvent / ThreatActor / Asset) kiezen
 *  we het meest-voorkomende base-type als default; modelleur kan
 *  later via attribuut-paneel het base-type wijzigen via stereotype-
 *  toggle. */
const STEREOTYPE_CHOICES: Array<{
  id: string;
  /** i18n-key onder ameffReview.choice* — voor zowel NL als EN. */
  labelKey: string;
  type_id: string;
  stereotype: string;
}> = [
  {
    id: "drawing",
    labelKey: "ameffReview.choiceDrawing",
    type_id: "drawing.Rectangle",
    stereotype: "",
  },
  {
    id: "risk.Risk",
    labelKey: "ameffReview.choiceRisk",
    type_id: "archimate.Assessment",
    stereotype: "risk.Risk",
  },
  {
    id: "risk.Vulnerability",
    labelKey: "ameffReview.choiceVulnerability",
    type_id: "archimate.Assessment",
    stereotype: "risk.Vulnerability",
  },
  {
    id: "risk.ThreatEvent",
    labelKey: "ameffReview.choiceThreatEvent",
    type_id: "archimate.BusinessEvent",
    stereotype: "risk.ThreatEvent",
  },
  {
    id: "risk.ThreatActor",
    labelKey: "ameffReview.choiceThreatActor",
    type_id: "archimate.BusinessActor",
    stereotype: "risk.ThreatActor",
  },
  {
    id: "risk.LossEvent",
    labelKey: "ameffReview.choiceLossEvent",
    type_id: "archimate.BusinessEvent",
    stereotype: "risk.LossEvent",
  },
  {
    id: "risk.ControlObjective",
    labelKey: "ameffReview.choiceControlObjective",
    type_id: "archimate.Goal",
    stereotype: "risk.ControlObjective",
  },
  {
    id: "risk.SecurityPrinciple",
    labelKey: "ameffReview.choiceSecurityPrinciple",
    type_id: "archimate.Principle",
    stereotype: "risk.SecurityPrinciple",
  },
  {
    id: "risk.Countermeasure",
    labelKey: "ameffReview.choiceCountermeasure",
    type_id: "archimate.Requirement",
    stereotype: "risk.Countermeasure",
  },
  {
    id: "risk.Asset",
    labelKey: "ameffReview.choiceAsset",
    type_id: "archimate.BusinessObject",
    stereotype: "risk.Asset",
  },
];

/** Map een (typeId, stereotypes) terug naar een choice-id voor de
 *  dropdown-default. Zoekt match op stereotype eerst (specifieker);
 *  zonder stereotype valt 'ie terug op "drawing". */
function choiceIdFor(typeId: string, stereotypes: string[]): string {
  for (const s of stereotypes) {
    const hit = STEREOTYPE_CHOICES.find((c) => c.stereotype === s);
    if (hit) return hit.id;
  }
  if (typeId === "drawing.Rectangle") return "drawing";
  return "drawing";
}

export function AmeffLabelReview({
  labels,
  diagrams,
  ambiguous = [],
  workspaceId,
  branchId,
  onClose,
}: {
  labels: AmeffLabel[];
  diagrams: AmeffDiagram[];
  ambiguous?: AmeffAmbiguous[];
  workspaceId: string;
  branchId: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const l of labels) {
      init[l.concept_id] = choiceIdFor(l.type_id, l.stereotypes);
    }
    return init;
  });
  const [diagramSelections, setDiagramSelections] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const d of diagrams) init[d.diagram_id] = d.view_kind;
    return init;
  });
  const [ambiguousSelections, setAmbiguousSelections] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const a of ambiguous) init[a.concept_id] = a.type_id;
    return init;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Volledige view-kind-lijst voor de diagram-dropdown. Bilingual-name
  // gesorteerd op NL-label voor leesbaarheid; geen filter op fase1
  // zodat de modelleur ook custom-workspace-view-kinds kan kiezen.
  const allViewKinds = useMemo(() => {
    return getRegistry()
      .listActiveViewKinds()
      .slice()
      .sort((a, b) => resolveBilingual(a.name, lang).localeCompare(resolveBilingual(b.name, lang)));
  }, [lang]);

  const baselineByConcept = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of labels) {
      m.set(l.concept_id, choiceIdFor(l.type_id, l.stereotypes));
    }
    return m;
  }, [labels]);

  const baselineByDiagram = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of diagrams) m.set(d.diagram_id, d.view_kind);
    return m;
  }, [diagrams]);

  const baselineByAmbiguous = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of ambiguous) m.set(a.concept_id, a.type_id);
    return m;
  }, [ambiguous]);

  const dirty = useMemo(() => {
    const labelDirty = Object.entries(selections).some(
      ([id, choice]) => baselineByConcept.get(id) !== choice,
    );
    const diagramDirty = Object.entries(diagramSelections).some(
      ([id, vk]) => baselineByDiagram.get(id) !== vk,
    );
    const ambiguousDirty = Object.entries(ambiguousSelections).some(
      ([id, typeId]) => baselineByAmbiguous.get(id) !== typeId,
    );
    return labelDirty || diagramDirty || ambiguousDirty;
  }, [
    selections,
    baselineByConcept,
    diagramSelections,
    baselineByDiagram,
    ambiguousSelections,
    baselineByAmbiguous,
  ]);

  /** Leesbaar label voor een type-id; valt terug op de kale id zodat een
   *  onbekend type zichtbaar blijft in plaats van als lege optie. */
  const typeLabel = (typeId: string): string => {
    const et = getRegistry().getElementType(typeId);
    return et ? resolveBilingual(et.label, lang) : typeId;
  };

  const applyAll = async (choiceId: string) => {
    const next: Record<string, string> = {};
    for (const l of labels) next[l.concept_id] = choiceId;
    setSelections(next);
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const ws = useWorkspaceData.getState();
      // Alleen daadwerkelijk gewijzigde items patchen. Server-side
      // valideert type+stereotype-compatibiliteit; bij fout valt het
      // hele apply-block niet om — we tonen alleen de eerste error.
      for (const l of labels) {
        const choiceId = selections[l.concept_id];
        if (!choiceId || choiceId === baselineByConcept.get(l.concept_id)) continue;
        const choice = STEREOTYPE_CHOICES.find((c) => c.id === choiceId);
        if (!choice) continue;
        await ws.patchConcept(workspaceId, branchId, l.concept_id, {
          type_id: choice.type_id,
          stereotypes: choice.stereotype ? [choice.stereotype] : [],
        });
      }
      // Idem voor diagrammen — alleen gewijzigde view-kind patchen.
      for (const d of diagrams) {
        const nextVk = diagramSelections[d.diagram_id];
        if (!nextVk || nextVk === baselineByDiagram.get(d.diagram_id)) continue;
        await ws.changeDiagramViewKind(workspaceId, branchId, d.diagram_id, nextVk);
      }
      // Ambigue conversies: alleen het basistype wijzigt. Het compat-
      // stereotype blijft ongemoeid — dat draagt de 3.1-identiteit en is
      // wat de AMEFF-export straks weer terugvertaalt. We sturen 'm dus
      // NIET mee in de patch.
      for (const a of ambiguous) {
        const nextType = ambiguousSelections[a.concept_id];
        if (!nextType || nextType === baselineByAmbiguous.get(a.concept_id)) continue;
        await ws.patchConcept(workspaceId, branchId, a.concept_id, { type_id: nextType });
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("ameffReview.errorFallback"));
    } finally {
      setBusy(false);
    }
  };

  if (labels.length === 0 && diagrams.length === 0 && ambiguous.length === 0) return null;

  return (
    <div className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl">
        <header className="border-(--ld-border) border-b px-4 py-2.5">
          <h2 className="font-semibold text-(--ld-text) text-sm">{t("ameffReview.title")}</h2>
          <p className="mt-0.5 text-(--ld-muted) text-xs">
            {[
              labels.length > 0 ? t("ameffReview.summaryLabels", { count: labels.length }) : null,
              diagrams.length > 0
                ? t("ameffReview.summaryDiagrams", { count: diagrams.length })
                : null,
              ambiguous.length > 0
                ? t("ameffReview.summaryAmbiguous", { count: ambiguous.length })
                : null,
            ]
              .filter((s): s is string => s !== null)
              .join(t("ameffReview.summaryAnd"))}
            {t("ameffReview.summarySuffix")}
          </p>
        </header>
        {/* Bulk-knoppen werken alleen op de Label-sectie; verbergen wanneer
            de modal uitsluitend voor ambigue conversies of diagrammen opent. */}
        <div
          className={`shrink-0 items-center gap-1.5 border-(--ld-border) border-b bg-(--ld-raised) px-4 py-1.5 text-xs ${
            labels.length > 0 ? "flex" : "hidden"
          }`}
        >
          <span className="text-(--ld-muted)">{t("ameffReview.bulk")}</span>
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 hover:bg-(--ld-raised)"
            onClick={() => applyAll("drawing")}
            disabled={busy}
          >
            {t("ameffReview.bulkAllDrawing")}
          </button>
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-1.5 py-0.5 hover:bg-(--ld-raised)"
            onClick={() => applyAll("risk.Risk")}
            disabled={busy}
          >
            {t("ameffReview.bulkAllRisk")}
          </button>
          <button
            type="button"
            className="ml-auto text-(--ld-muted) hover:text-(--ld-text)"
            onClick={() =>
              setSelections(
                Object.fromEntries(
                  labels.map((l) => [l.concept_id, choiceIdFor(l.type_id, l.stereotypes)]),
                ),
              )
            }
            disabled={busy}
          >
            {t("ameffReview.bulkReset")}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {labels.length > 0 && (
            <h3 className="mb-1 text-(--ld-muted) text-xs">{t("ameffReview.sectionLabels")}</h3>
          )}
          {labels.length > 0 && (
            <table className="w-full table-fixed text-xs">
              <thead className="text-(--ld-muted)">
                <tr>
                  <th className="w-12 py-1 text-left font-medium">{t("ameffReview.colSource")}</th>
                  <th className="w-16 py-1 text-left font-medium">{t("ameffReview.colKind")}</th>
                  <th className="py-1 text-left font-medium">{t("ameffReview.colName")}</th>
                  <th className="w-56 py-1 text-left font-medium">
                    {t("ameffReview.colTypeStereotype")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {labels.map((l) => {
                  const choiceId = selections[l.concept_id] ?? "drawing";
                  const isHeuristic = l.source === "heuristic";
                  return (
                    <tr key={l.concept_id} className="border-(--ld-rule) border-t">
                      <td className="py-1.5 text-xs">
                        <span
                          className={
                            isHeuristic
                              ? "rounded bg-(--ld-active) px-1 text-(--ld-accent)"
                              : "rounded bg-(--ld-active) px-1 text-(--ld-muted)"
                          }
                          title={
                            isHeuristic
                              ? t("ameffReview.chipAutoTitleLabel")
                              : t("ameffReview.chipNoneTitle")
                          }
                        >
                          {isHeuristic ? t("ameffReview.chipAuto") : t("ameffReview.chipNone")}
                        </span>
                      </td>
                      <td className="py-1.5 text-xs">
                        <span
                          className="rounded bg-(--ld-positive-bg) px-1 text-(--ld-positive-ink)"
                          title={t("ameffReview.chipElementTitle")}
                        >
                          {t("ameffReview.chipElement")}
                        </span>
                      </td>
                      <td className="truncate py-1.5 pr-2 text-(--ld-text)" title={l.name}>
                        {l.name}
                      </td>
                      <td className="py-1.5">
                        <select
                          className="w-full rounded border border-(--ld-control) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none"
                          value={choiceId}
                          disabled={busy}
                          onChange={(e) =>
                            setSelections((prev) => ({ ...prev, [l.concept_id]: e.target.value }))
                          }
                        >
                          {STEREOTYPE_CHOICES.map((c) => (
                            <option key={c.id} value={c.id}>
                              {t(c.labelKey)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {diagrams.length > 0 && (
            <>
              <h3 className="mt-3 mb-1 text-(--ld-muted) text-xs">
                {t("ameffReview.sectionDiagrams")}
              </h3>
              <table className="w-full table-fixed text-xs">
                <thead className="text-(--ld-muted)">
                  <tr>
                    <th className="w-12 py-1 text-left font-medium">
                      {t("ameffReview.colSource")}
                    </th>
                    <th className="w-16 py-1 text-left font-medium">{t("ameffReview.colKind")}</th>
                    <th className="py-1 text-left font-medium">{t("ameffReview.colName")}</th>
                    <th className="w-56 py-1 text-left font-medium">
                      {t("ameffReview.colDiagramType")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {diagrams.map((d) => {
                    const currentVk = diagramSelections[d.diagram_id] ?? d.view_kind;
                    return (
                      <tr key={d.diagram_id} className="border-(--ld-rule) border-t">
                        <td className="py-1.5 text-xs">
                          <span
                            className="rounded bg-(--ld-active) px-1 text-(--ld-accent)"
                            title={t("ameffReview.chipAutoTitleView")}
                          >
                            {t("ameffReview.chipAuto")}
                          </span>
                        </td>
                        <td className="py-1.5 text-xs">
                          <span
                            className="rounded bg-(--ld-active) px-1 text-(--ld-text)"
                            title={t("ameffReview.chipViewTitle")}
                          >
                            {t("ameffReview.chipView")}
                          </span>
                        </td>
                        <td className="truncate py-1.5 pr-2 text-(--ld-text)" title={d.name}>
                          {d.name}
                        </td>
                        <td className="py-1.5">
                          <select
                            className="w-full rounded border border-(--ld-control) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none"
                            value={currentVk}
                            disabled={busy}
                            onChange={(e) =>
                              setDiagramSelections((prev) => ({
                                ...prev,
                                [d.diagram_id]: e.target.value,
                              }))
                            }
                          >
                            {allViewKinds.find((v) => v.id === currentVk) === undefined && (
                              <option value={currentVk}>
                                {currentVk}
                                {t("ameffReview.unknownSuffix")}
                              </option>
                            )}
                            {allViewKinds.map((vk) => (
                              <option key={vk.id} value={vk.id}>
                                {resolveBilingual(vk.name, lang)}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
          {ambiguous.length > 0 && (
            <>
              <h3 className="mt-3 mb-1 text-(--ld-muted) text-xs">
                {t("ameffReview.sectionAmbiguous")}
              </h3>
              <p className="mb-1 text-xs text-(--ld-muted)">{t("ameffReview.ambiguousHint")}</p>
              <table className="w-full table-fixed text-xs">
                <thead className="text-(--ld-muted)">
                  <tr>
                    <th className="w-12 py-1 text-left font-medium">
                      {t("ameffReview.colSource")}
                    </th>
                    <th className="w-24 py-1 text-left font-medium">
                      {t("ameffReview.colLegacyType")}
                    </th>
                    <th className="py-1 text-left font-medium">{t("ameffReview.colName")}</th>
                    <th className="w-56 py-1 text-left font-medium">
                      {t("ameffReview.colTargetType")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ambiguous.map((a) => {
                    const current = ambiguousSelections[a.concept_id] ?? a.type_id;
                    return (
                      <tr key={a.concept_id} className="border-(--ld-rule) border-t">
                        <td className="py-1.5 text-xs">
                          <span
                            className="rounded bg-(--ld-notice-bg) px-1 text-(--ld-notice-ink)"
                            title={t("ameffReview.chipDefaultTitle")}
                          >
                            {t("ameffReview.chipDefault")}
                          </span>
                        </td>
                        <td className="py-1.5 text-xs">
                          <span className="rounded bg-(--ld-active) px-1 text-(--ld-muted)">
                            {typeLabel(a.legacy_type_id)}
                          </span>
                        </td>
                        <td className="truncate py-1.5 pr-2 text-(--ld-text)" title={a.name}>
                          {a.name}
                        </td>
                        <td className="py-1.5">
                          <select
                            className="w-full rounded border border-(--ld-control) px-1 py-0.5 text-xs focus:border-(--ld-accent) focus:outline-none"
                            value={current}
                            disabled={busy}
                            onChange={(e) =>
                              setAmbiguousSelections((prev) => ({
                                ...prev,
                                [a.concept_id]: e.target.value,
                              }))
                            }
                          >
                            {a.candidates.map((typeId, idx) => (
                              <option key={typeId} value={typeId}>
                                {typeLabel(typeId)}
                                {idx === 0 ? t("ameffReview.defaultSuffix") : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
        {error && (
          <div className="border-(--ld-control) border-t bg-(--ld-negative-bg) px-4 py-1.5 text-(--ld-negative) text-xs">
            {error}
          </div>
        )}
        <footer className="flex items-center justify-end gap-2 border-(--ld-border) border-t bg-(--ld-raised) px-4 py-2">
          <button
            type="button"
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-0.5 text-(--ld-text) text-xs hover:bg-(--ld-raised)"
            onClick={onClose}
            disabled={busy}
          >
            {t("ameffReview.close")}
          </button>
          <button
            type="button"
            className="rounded bg-(--ld-primary) px-2 py-0.5 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            onClick={apply}
            disabled={busy || !dirty}
          >
            {busy ? t("ameffReview.applying") : t("ameffReview.apply")}
          </button>
        </footer>
      </div>
    </div>
  );
}
